/**
 * Budget Calculations
 *
 * Pure functions for budget-related calculations.
 * These are storage-agnostic and can be used by any storage provider
 * (local SQL, remote API, etc.)
 */

import type { Transaction } from '../types';
import type { Budget, BudgetStatus } from '../types/budget';

/**
 * Month range for filtering transactions
 */
export type MonthRange = {
  start: Date;
  end: Date;
  year: number;
  month: number;
};

/**
 * Parse month string (YYYY-MM) into date range
 *
 * @param monthStr - Month string in YYYY-MM format
 * @returns Month range with start and end dates
 *
 * @example
 * parseMonthRange('2024-01')
 * // Returns: {
 * //   start: Date(2024, 0, 1, 0, 0, 0),
 * //   end: Date(2024, 0, 31, 23, 59, 59),
 * //   year: 2024,
 * //   month: 1
 * // }
 */
export function parseMonthRange(monthStr: string): MonthRange {
  const [year, monthNum] = monthStr.split('-').map(Number);
  const start = new Date(year, monthNum - 1, 1, 0, 0, 0);
  const end = new Date(year, monthNum, 0, 23, 59, 59);

  return { start, end, year, month: monthNum };
}

/**
 * Filter transactions within a month range
 *
 * @param transactions - All transactions
 * @param range - Month range to filter by
 * @param excludeExcluded - Exclude transactions marked as excluded (default: true)
 * @returns Transactions within the month range
 */
export function filterTransactionsByMonth(
  transactions: Transaction[],
  range: MonthRange,
  excludeExcluded: boolean = true
): Transaction[] {
  return transactions.filter(t => {
    const tDate = new Date(t.date);
    const inRange = tDate >= range.start && tDate <= range.end;
    const notExcluded = !excludeExcluded || !t.excluded;
    return inRange && notExcluded;
  });
}

/**
 * Calculate spending by category
 *
 * @param transactions - Transactions to analyze
 * @returns Map of category to total spent (absolute value)
 */
export function calculateSpendingByCategory(transactions: Transaction[]): Map<string, number> {
  const spentByCategory = new Map<string, number>();

  for (const t of transactions) {
    const current = spentByCategory.get(t.category) || 0;
    spentByCategory.set(t.category, current + Math.abs(t.amount));
  }

  return spentByCategory;
}

/**
 * Calculate budget status for a single budget
 *
 * @param budget - Budget configuration
 * @param spentByCategory - Map of category spending
 * @returns Budget status with calculated fields
 */
export function calculateBudgetStatus(
  budget: Budget,
  spentByCategory: Map<string, number>
): BudgetStatus {
  // Sum spending across all budget categories
  let spent = 0;
  for (const category of budget.categories) {
    spent += spentByCategory.get(category) || 0;
  }

  const rollover = budget.rolloverAmount || 0;
  const remaining = budget.limit - spent + rollover;
  const percentage = budget.limit > 0 ? (spent / budget.limit) * 100 : 0;
  const totalLimit = budget.limit + rollover;

  return {
    name: budget.name,
    categories: budget.categories,
    type: budget.type,
    limit: budget.limit,
    spent,
    remaining,
    percentage,
    allowRollover: budget.allowRollover,
    resetRolloverYearly: budget.resetRolloverYearly,
    isFixedExpense: budget.isFixedExpense,
    rolloverAmount: rollover,
    totalLimit
  };
}

/**
 * Group budgets by type
 *
 * @param budgetStatuses - Array of budget statuses
 * @returns Budgets grouped by type (monthly, quarterly, yearly)
 */
export function groupBudgetsByType(budgetStatuses: BudgetStatus[]): {
  monthly: BudgetStatus[];
  quarterly: BudgetStatus[];
  yearly: BudgetStatus[];
} {
  const monthly: BudgetStatus[] = [];
  const quarterly: BudgetStatus[] = [];
  const yearly: BudgetStatus[] = [];

  for (const status of budgetStatuses) {
    if (status.type === 'monthly') {
      monthly.push(status);
    } else if (status.type === 'quarterly') {
      quarterly.push(status);
    } else if (status.type === 'yearly') {
      yearly.push(status);
    }
  }

  return { monthly, quarterly, yearly };
}

/**
 * Calculate unbudgeted spending
 *
 * @param spentByCategory - Map of category spending
 * @param budgetedCategories - Set of categories that are budgeted
 * @returns Unbudgeted status or null if no unbudgeted spending
 */
export function calculateUnbudgetedSpending(
  spentByCategory: Map<string, number>,
  budgetedCategories: Set<string>
): BudgetStatus | null {
  const unbudgetedCategories: string[] = [];
  let totalUnbudgetedSpent = 0;

  for (const [category, spent] of spentByCategory.entries()) {
    if (!budgetedCategories.has(category)) {
      unbudgetedCategories.push(category);
      totalUnbudgetedSpent += spent;
    }
  }

  if (unbudgetedCategories.length === 0) {
    return null;
  }

  return {
    name: 'Unbudgeted',
    categories: unbudgetedCategories,
    type: 'unbudgeted',
    limit: 0,
    spent: totalUnbudgetedSpent,
    remaining: -totalUnbudgetedSpent,
    percentage: 0,
    isUnbudgeted: true
  };
}

/**
 * Get set of all budgeted categories
 *
 * @param budgets - Array of budgets
 * @returns Set of category names that are in budgets
 */
export function getBudgetedCategories(budgets: Budget[]): Set<string> {
  const categories = new Set<string>();

  for (const budget of budgets) {
    for (const category of budget.categories) {
      categories.add(category);
    }
  }

  return categories;
}

/**
 * Calculate total spending across all transactions
 *
 * @param transactions - Transactions to analyze
 * @returns Total spending (absolute value)
 */
export function calculateTotalSpending(transactions: Transaction[]): number {
  return transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);
}

/**
 * Calculate budget utilization percentage
 *
 * @param spent - Amount spent
 * @param limit - Budget limit
 * @returns Percentage (0-100+)
 */
export function calculateUtilizationPercentage(spent: number, limit: number): number {
  if (limit <= 0) return 0;
  return (spent / limit) * 100;
}

/**
 * Check if budget is over limit
 *
 * @param status - Budget status
 * @returns True if over limit
 */
export function isBudgetOverLimit(status: BudgetStatus): boolean {
  return status.remaining < 0;
}

/**
 * Check if budget is near limit (within threshold)
 *
 * @param status - Budget status
 * @param threshold - Percentage threshold (default: 90)
 * @returns True if near limit
 */
export function isBudgetNearLimit(status: BudgetStatus, threshold: number = 90): boolean {
  return status.percentage >= threshold && !isBudgetOverLimit(status);
}

/**
 * Get budget status category (ok, warning, danger)
 *
 * @param status - Budget status
 * @param warningThreshold - Warning threshold percentage (default: 80)
 * @param dangerThreshold - Danger threshold percentage (default: 100)
 * @returns Status category
 */
export function getBudgetStatusCategory(
  status: BudgetStatus,
  warningThreshold: number = 80,
  dangerThreshold: number = 100
): 'ok' | 'warning' | 'danger' {
  if (status.percentage >= dangerThreshold) {
    return 'danger';
  }
  if (status.percentage >= warningThreshold) {
    return 'warning';
  }
  return 'ok';
}
